import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ChevronRight, Loader2, MessageSquare, Package, ArrowRight, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { MilestoneTimeline } from '@/components/MilestoneTracker';
import { GlassCard, API } from './GlassCard';
import ItemOrders from './ItemOrders';

const statusColors = {
  pending: 'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20',
  pending_payment: 'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20',
  accepted: 'bg-blue-500/15 text-[#93C5FD] border-blue-500/20',
  booked: 'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20',
  in_progress: 'bg-blue-500/15 text-[#93C5FD] border-blue-500/20',
  shipped: 'bg-purple-500/15 text-[#C4B5FD] border-purple-500/20',
  delivered: 'bg-purple-500/15 text-[#C4B5FD] border-purple-500/20',
  completed: 'bg-green-500/15 text-[#86EFAC] border-green-500/20',
  cancelled: 'bg-red-500/15 text-[#FCA5A5] border-red-500/20',
  disputed: 'bg-red-500/15 text-[#FCA5A5] border-red-500/20',
};

export default function Orders() {
  const { getAuthHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [milestones, setMilestones] = useState({});
  const [activeTab, setActiveTab] = useState('items');  // items | services | custom

  useEffect(() => {
    fetchOrders();
    fetchCustomOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/bookings/my-bookings?role=vendor`, { headers: getAuthHeaders() });
      if (response.ok) setBookings(await response.json());
    } catch {}
  };

  const fetchCustomOrders = async () => {
    try {
      const response = await fetch(`${API}/custom-orders/mine?role=vendor`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCustomOrders(data.orders || []);
      }
    } catch {}
  };

  const fetchMilestones = async (bookingId) => {
    try {
      const response = await fetch(`${API}/milestones/booking/${bookingId}/`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setMilestones((prev) => ({ ...prev, [bookingId]: data }));
      }
    } catch {}
  };

  const toggleOrder = (bookingId) => {
    if (expandedOrder === bookingId) { setExpandedOrder(null); return; }
    setExpandedOrder(bookingId);
    if (!milestones[bookingId]) fetchMilestones(bookingId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="orders-title">Orders</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">Shopify-style item sales, service bookings and custom orders in one place</p>
      </div>

      {/* ─── Tab switcher ─── */}
      <div className="flex gap-1.5 border-b border-white/10 overflow-x-auto" data-testid="orders-tabs">
        {[
          { id: 'items',    label: 'Item Orders',    icon: ShoppingBag },
          { id: 'services', label: 'Service Bookings', icon: Calendar },
          { id: 'custom',   label: `Custom Orders${customOrders.length ? ' · ' + customOrders.length : ''}`, icon: Package },
        ].map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${active ? 'text-white border-[#8B5CF6]' : 'text-[#9CA3AF] border-transparent hover:text-white'}`}
              data-testid={`orders-tab-${tab.id}`}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ─── Items tab ─── */}
      {activeTab === 'items' && <ItemOrders />}

      {/* ─── Custom Orders tab ─── */}
      {activeTab === 'custom' && (
        customOrders.length > 0 ? (
          <div className="space-y-3" data-testid="custom-orders-section">
            {customOrders.map((co) => {
              const statusMap = {
                pending_payment:    { label: 'Awaiting Payment',  cls: 'bg-amber-500/15 text-amber-200 border-amber-400/30' },
                active:             { label: 'In Progress',       cls: 'bg-blue-500/15 text-blue-200 border-blue-400/30' },
                delivered:          { label: 'Delivered · Waiting', cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30' },
                revision_requested: { label: 'Revision Requested', cls: 'bg-orange-500/15 text-orange-200 border-orange-400/30' },
                completed:          { label: 'Completed',         cls: 'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30' },
                cancelled:          { label: 'Cancelled',         cls: 'bg-red-500/15 text-red-200 border-red-400/30' },
              }[co.status] || { label: co.status, cls: 'bg-white/5 text-zinc-200 border-white/10' };
              return (
                <Link
                  key={co.order_id}
                  to={`/dashboard/vendor/orders/${co.order_id}`}
                  className="block rounded-2xl bg-[#13153A]/70 border border-white/10 hover:border-fuchsia-400/40 p-4 transition-all group"
                  data-testid={`custom-order-${co.order_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusMap.cls}`}>
                          {statusMap.label}
                        </span>
                        {co.status === 'pending_payment' && (
                          <span className="text-[10px] text-amber-300 font-bold animate-pulse">⏳ Waiting on buyer</span>
                        )}
                        {co.status === 'delivered' && co.auto_complete_at && (
                          <span className="text-[10px] text-emerald-300">Auto-completes {new Date(co.auto_complete_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      <p className="text-white font-semibold truncate group-hover:text-fuchsia-200 transition-colors">{co.title}</p>
                      <p className="text-zinc-400 text-xs mt-0.5">
                        ${co.price.toFixed(2)} · {co.delivery_days}d · created {new Date(co.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-zinc-500 group-hover:text-fuchsia-300 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <GlassCard className="text-center py-12 px-6">
            <Package size={40} className="mx-auto mb-4 text-[#9CA3AF] opacity-40" />
            <p className="text-[#9CA3AF]">No custom orders yet</p>
          </GlassCard>
        )
      )}

      {/* ─── Service bookings tab ─── */}
      {activeTab === 'services' && (
        bookings.length > 0 ? (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <GlassCard key={booking.booking_id} className="overflow-hidden" data-testid={`order-${booking.booking_id}`}>
                <button onClick={() => toggleOrder(booking.booking_id)} className="w-full p-5 flex items-center justify-between hover:bg-white/5 transition-all text-left">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6]/40 to-[#3B82F6]/40 flex items-center justify-center text-sm font-bold text-white">
                      {booking.consumer_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{booking.service_title || 'Order'}</p>
                      <p className="text-[#64748B] text-xs">{booking.consumer_name} · {booking.created_at ? new Date(booking.created_at).toLocaleDateString() : '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[#A78BFA] font-bold">${booking.price}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColors[booking.status] || statusColors.pending}`}>
                      {(booking.status || 'pending').replace(/_/g, ' ')}
                    </span>
                    <ChevronRight size={16} className={`text-[#9CA3AF] transition-transform ${expandedOrder === booking.booking_id ? 'rotate-90' : ''}`} />
                  </div>
                </button>

                {expandedOrder === booking.booking_id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="border-t border-white/5 p-5">
                    {milestones[booking.booking_id] ? (
                      <>
                        <MilestoneTimeline
                          milestones={milestones[booking.booking_id].milestones}
                          currentStep={milestones[booking.booking_id].current_step}
                          isVendor={true}
                          bookingId={booking.booking_id}
                          trackingNumber={milestones[booking.booking_id].tracking_number}
                          progressPercent={milestones[booking.booking_id].progress_percent}
                          progressLabel={milestones[booking.booking_id].progress_label}
                          revisionsUsed={milestones[booking.booking_id].revisions_used}
                          revisionsLimit={milestones[booking.booking_id].revisions_limit}
                          revisionsRemaining={milestones[booking.booking_id].revisions_remaining}
                          onUpdate={() => { fetchMilestones(booking.booking_id); fetchOrders(); }}
                        />
                        <div className="mt-4 flex gap-2">
                          <Link to={`/messages/${booking.consumer_id}`}>
                            <Button variant="outline" className="border-white/10 text-[#9CA3AF] hover:text-white text-xs"><MessageSquare size={12} className="mr-1.5" /> Message Client</Button>
                          </Link>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[#8B5CF6]" /></div>
                    )}
                  </motion.div>
                )}
              </GlassCard>
            ))}
          </div>
        ) : (
          <GlassCard className="text-center py-12 px-6">
            <Calendar size={40} className="mx-auto mb-4 text-[#9CA3AF] opacity-40" />
            <p className="text-[#9CA3AF]">No service bookings yet</p>
            <p className="text-[#9CA3AF]/60 text-xs mt-1">
              Service bookings from buyer inquiries will appear here once you accept them.
            </p>
          </GlassCard>
        )
      )}
    </div>
  );
}
